// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: every user-visible string of the calendar, in one place, word for word calendar-plan.md section 12.
// CalendarStrings follows the shared table (calendar_strings_table.md, Obyrus v2026.10.122's copy) in its order,
// leaving out the rows the browser has no place for and the ones removed on 2026-11-02; CalendarBrowserStrings is the browser/desktop-only table
// (calendar_strings_browser_table.md). <name>-style placeholders stay in the text and are filled by fill().
// English only, like the relocate panel. The server's messages are shown as the API returns them, so they are not
// here. Exclude from upstream sync.

export const CalendarStrings = {
	calendar: 'Calendar',
	today: 'Today',
	agenda: 'Agenda',
	month: 'Month',
	newEvent: 'New event',
	todayHeading: 'Today · <date>',
	tomorrowHeading: 'Tomorrow · <date>',
	past: 'Past',
	noUpcomingEvents: 'No upcoming events',
	allDay: 'All day',
	addedBy: 'Added by <name>',
	fromPhone: "From <name>'s phone",
	someone: 'Someone',
	readOnly: 'Read-only',
	previousMonth: 'Previous month',
	nextMonth: 'Next month',
	editEvent: 'Edit event',
	cancel: 'Cancel',
	save: 'Save',
	title: 'Title',
	starts: 'Starts',
	ends: 'Ends',
	timeRange: '<start> → <end>',
	reminderBanner: 'Reminder banner',
	remind: 'Remind',
	none: 'None',
	colour: 'Colour',
	memberColour: "<name>'s colour",
	customColour: 'Custom colour',
	customColourPlaceholder: '#rrggbb',
	customColourInvalid: 'Enter a colour as #rrggbb.',
	notes: 'Notes',
	delete: 'Delete',
	deleteTitle: 'Delete this event?',
	deleteText: 'It will be removed from the calendar for both of you.',
	conflict: '<name> changed this event',
	keepMine: 'Keep mine',
	useTheirs: 'Use theirs',
	requestFailedStatus: 'The request failed (HTTP <status>).',
	requestFailed: 'The request failed.',
	remind5: '5 min before',
	remind15: '15 min before',
	remind30: '30 min before',
	remind60: '1 hour before',
	remind120: '2 hours before',
	remind1440: '1 day before',
	remind2880: '2 days before',
	bell5: '5 min before',
	bell15: '15 min before',
	bell30: '30 min before',
	bell60: '1 h before',
	bell120: '2 h before',
	bell1440: '1 day before',
	bell2880: '2 days before',
	swatchAmber: 'Amber',
	swatchSky: 'Sky',
	swatchRed: 'Red',
	swatchSage: 'Sage',
	swatchViolet: 'Violet',
	swatchTeal: 'Teal',
	swatchPink: 'Pink',
	swatchSlate: 'Slate',
	weekdaySunday: 'S',
	weekdayMonday: 'M',
	weekdayTuesday: 'T',
	weekdayWednesday: 'W',
	weekdayThursday: 'T',
	weekdayFriday: 'F',
	weekdaySaturday: 'S',
} as const;

export const CalendarBrowserStrings = {
	people: 'People',
	bannerTimedToday: '<title> today at <time> · in <relative>',
	bannerTimedTomorrow: '<title> tomorrow at <time> · in <relative>',
	bannerTimedLater: '<title> <date> at <time> · in <relative>',
	bannerTimedNow: '<title> today at <time> · now',
	bannerAllDayToday: '<title> today',
	bannerAllDayTomorrow: '<title> tomorrow',
	bannerAllDayLater: '<title> <date>',
	relativeMinutes: '<n> min',
	relativeHours: '<h> h',
	relativeHoursMinutes: '<h> h <m> min',
	relativeOneDay: '1 day',
	relativeDays: '<n> days',
	more: '+<n> more',
	openCalendar: 'Open calendar',
	dismissOnThisDevice: 'Dismiss on this device',
	addOn: 'Add on <day>',
	hour: 'Hour',
	minute: 'Minute',
	period: 'AM/PM',
} as const;

/** One pass, so a value (a title, a name) is never read as a placeholder itself. */
export function fill(template: string, values: Readonly<Record<string, string | number>>): string {
	return template.replace(/<([a-z]+)>/g, (match, key: string) => (key in values ? String(values[key]) : match));
}

/** The swatches, in the table's order: the accessibility label and the colour. */
export const CALENDAR_SWATCHES: ReadonlyArray<{readonly label: string; readonly color: string}> = [
	{label: CalendarStrings.swatchAmber, color: '#e8a33d'},
	{label: CalendarStrings.swatchSky, color: '#4fb3d9'},
	{label: CalendarStrings.swatchRed, color: '#e5604f'},
	{label: CalendarStrings.swatchSage, color: '#7fb58a'},
	{label: CalendarStrings.swatchViolet, color: '#a07ce8'},
	{label: CalendarStrings.swatchTeal, color: '#3fb8a8'},
	{label: CalendarStrings.swatchPink, color: '#e06fa8'},
	{label: CalendarStrings.swatchSlate, color: '#8a93a3'},
];

/** The sheet's Remind list: None first, then the server's lead times; 30 min is the default. */
export const CALENDAR_REMIND_OPTIONS: ReadonlyArray<{readonly minutes: number | null; readonly label: string}> = [
	{minutes: null, label: CalendarStrings.none},
	{minutes: 5, label: CalendarStrings.remind5},
	{minutes: 15, label: CalendarStrings.remind15},
	{minutes: 30, label: CalendarStrings.remind30},
	{minutes: 60, label: CalendarStrings.remind60},
	{minutes: 120, label: CalendarStrings.remind120},
	{minutes: 1440, label: CalendarStrings.remind1440},
	{minutes: 2880, label: CalendarStrings.remind2880},
];

const BELL_LEADS: Readonly<Record<number, string>> = {
	5: CalendarStrings.bell5,
	15: CalendarStrings.bell15,
	30: CalendarStrings.bell30,
	60: CalendarStrings.bell60,
	120: CalendarStrings.bell120,
	1440: CalendarStrings.bell1440,
	2880: CalendarStrings.bell2880,
};

/** An agenda card's bell text; null when there is no lead time (or one the table has no text for). */
export function bellLeadText(minutes: number | null): string | null {
	return minutes == null ? null : (BELL_LEADS[minutes] ?? null);
}

/** Sunday … Saturday, as week_start numbers them (0 = Sunday). */
export const WEEKDAY_INITIALS: ReadonlyArray<string> = [
	CalendarStrings.weekdaySunday,
	CalendarStrings.weekdayMonday,
	CalendarStrings.weekdayTuesday,
	CalendarStrings.weekdayWednesday,
	CalendarStrings.weekdayThursday,
	CalendarStrings.weekdayFriday,
	CalendarStrings.weekdaySaturday,
];

export const CALENDAR_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;
